import React, { useState, useContext } from "react";
import { AnimatePresence, motion } from "motion/react";
import cx from "classnames";
import { flip, shift, useDismiss, useFloating, autoUpdate, useInteractions } from "@floating-ui/react";

import { t } from "i18next";
import { MediaQueriesContext } from "@/App";
import { Icon } from "Icons/index";

import s from "./dropdownMenu.scss";
import m from "./dropdownMenuMobile.scss";

/**
 * Action properties
 *
 * @typedef {Object} DropdownMenuAction
 * @property {function} onClick - Callback function to handle click events.
 * @property {React.ReactNode} icon - Icon element to display in the dropdown menu.
 * @property {string | React.ReactElement} label - Text or Element label for the dropdown menu item.
 * @property {{icon?: string, label?: string, container?: string}} [styles] - Custom classNames to be applied to icon and label.
 * @property {boolean} [isHidden] - Item should be hidden from the list.
 */

/**
 * DropdownMenu component.
 *
 * @param {Object} props - Component properties.
 * @param {function(boolean):void} onOpenChange - Callback function called when the open state changes. Receives the new open state as a boolean.
 * @param {boolean} isOpen - Indicates whether the dropdown menu is currently open.
 * @param {boolean} [hideToggle] - If true, hide the toggle given as the children
 * @param {Array<DropdownMenuAction>} actions - List of action objects to be displayed in the dropdown menu.
 * @param {React.ReactNode} [children] - Element to be rendered as action to open/close the Dropdown
 */
const DropdownMenu = (props) => {
  const { isMediaMobile } = useContext(MediaQueriesContext);

  const USE_MOBILE = isMediaMobile;
  const USE_DESKTOP = !isMediaMobile;

  const handleOnToggleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    props.onOpenChange(!props.isOpen);
  };

  const toggleButton =
    props.children && !props.hideToggle ? (
      <div className={s.displayContents} onClick={handleOnToggleClick}>
        {props.children}
      </div>
    ) : (
      <span />
    );

  const visibleActions = props.actions.filter((action) => !action.isHidden);

  return (
    <>
      {USE_DESKTOP && <DropdownMenuDesktop {...props} toggle={toggleButton} actions={visibleActions} />}
      {USE_MOBILE && <DropdownMenuMobile {...props} toggle={toggleButton} actions={visibleActions} />}
    </>
  );
};

export const DropdownMenuMobile = (props) => {
  const TRANSITION_DURATION_S = 0.33;

  const [isClickable, setIsClickable] = useState(false);

  const handleCloseDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    props.onOpenChange(false);
  };

  return (
    <>
      {props.toggle}
      <AnimatePresence>
        {props.isOpen && (
          <div className={cx(m.dropdownMobile, m.screenLayer, !isClickable && m.notClickable)}>
            <motion.div
              className={cx(m.background, m.screenLayer)}
              onClick={handleCloseDropdown}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: TRANSITION_DURATION_S }}
            />
            <motion.div
              className={cx(m.dropdownMenuMobile, m.menuLayer)}
              onAnimationStart={() => setIsClickable(false)}
              onAnimationComplete={() => setIsClickable(props.isOpen)}
              initial={{ opacity: 0, y: "133%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "133%" }}
              transition={{ duration: TRANSITION_DURATION_S }}
            >
              <div className={s.dropdownMenu} onClick={handleCloseDropdown}>
                {props.actions.map((action, i) => (
                  <DropdownItem styles={{ container: m.item, label: m.label }} {...action} key={i} />
                ))}
              </div>
              <div className={cx(s.dropdownMenu, m.cancelContainer)}>
                <DropdownItem
                  styles={{ container: cx(m.item, m.itemCancel), label: cx(m.label, m.cancel) }}
                  label={t("ddMenu.cancel")}
                  onClick={handleCloseDropdown}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

const DropdownMenuDesktop = (props) => {
  const { refs, floatingStyles, context } = useFloating({
    open: props.isOpen,
    onOpenChange: props.onOpenChange,
    middleware: [flip(), shift()],
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const handleCloseDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    props.onOpenChange(false);
  };

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {props.toggle}
      </div>
      {props.isOpen && (
        <div className={s.dropdown} ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <div className={s.dropdownMenu} onClick={handleCloseDropdown}>
            {props.actions.map((action, i) => (
              <DropdownItem {...action} key={i} />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const DropdownItem = (props) => {
  const isLabelAString = typeof props.label === "string";
  const isLabelAnObject = typeof props.label === "object";

  const containerClassName = cx(s.item, s.displayContents, props.styles?.container);
  const iconClassName = cx(s.itemIcon, props.styles?.icon);
  const labelClassName = cx(s.itemLabel, props.labelClassNames, props.styles?.label);

  return (
    <div className={containerClassName} onClick={props.onClick}>
      {props.icon && <Icon className={iconClassName} svg={props.icon} />}
      {!!props.label && isLabelAString && <div className={labelClassName}>{props.label}</div>}
      {!!props.label && isLabelAnObject && <>{props.label}</>}
    </div>
  );
};

export default DropdownMenu;
